import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { tilesApi, tileOperationsApi } from '../../services/api';

// Async thunks
export const fetchTiles = createAsyncThunk(
  'tiles/fetchAll',
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await tilesApi.getAll(params);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchTileById = createAsyncThunk(
  'tiles/fetchById',
  async (id, { rejectWithValue }) => {
    try {
      const response = await tilesApi.getById(id);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const createTile = createAsyncThunk(
  'tiles/create',
  async (data, { rejectWithValue }) => {
    try {
      const response = await tilesApi.create(data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateTile = createAsyncThunk(
  'tiles/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await tilesApi.update(id, data);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const deleteTile = createAsyncThunk(
  'tiles/delete',
  async (id, { rejectWithValue }) => {
    try {
      await tilesApi.delete(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const uploadTile = createAsyncThunk(
  'tiles/upload',
  async (formData, { rejectWithValue }) => {
    try {
      const response = await tileOperationsApi.upload(formData);
      return response;
    } catch (error) {
      return rejectWithValue(error.response?.data || error.message);
    }
  }
);

const tilesSlice = createSlice({
  name: 'tiles',
  initialState: {
    items: [],
    currentItem: null,
    pagination: {
      page: 1,
      limit: 50,
      total: 0,
      pages: 0,
    },
    loading: false,
    error: null,
  },
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentItem: (state) => {
      state.currentItem = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch all tiles
      .addCase(fetchTiles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTiles.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload.items || action.payload;
        if (action.payload.pagination) {
          state.pagination = action.payload.pagination;
        }
      })
      .addCase(fetchTiles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch tile by ID
      .addCase(fetchTileById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTileById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentItem = action.payload;
      })
      .addCase(fetchTileById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create tile
      .addCase(createTile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createTile.fulfilled, (state, action) => {
        state.loading = false;
        state.items.unshift(action.payload);
      })
      .addCase(createTile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Update tile
      .addCase(updateTile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateTile.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.items.findIndex(item => item._id === action.payload._id);
        if (index !== -1) {
          state.items[index] = action.payload;
        }
        if (state.currentItem?._id === action.payload._id) {
          state.currentItem = action.payload;
        }
      })
      .addCase(updateTile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete tile
      .addCase(deleteTile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteTile.fulfilled, (state, action) => {
        state.loading = false;
        state.items = state.items.filter(item => item._id !== action.payload);
        if (state.currentItem?._id === action.payload) {
          state.currentItem = null;
        }
      })
      .addCase(deleteTile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Upload tile
      .addCase(uploadTile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(uploadTile.fulfilled, (state, action) => {
        state.loading = false;
        state.items.unshift(action.payload);
      })
      .addCase(uploadTile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearError, clearCurrentItem } = tilesSlice.actions;
export default tilesSlice.reducer;
